/**
 * HistoryDrawer — slide-over from the RIGHT, behind the icon rail.
 */
import { useEffect, useRef } from "react";
import { History as HistoryIcon, X } from "lucide-react";
import toast from "react-hot-toast";
import { openV2Session } from "@/services/multi-agent/laneV2/openV2Session";
import { useLaneV2SessionStore } from "@/stores/laneV2SessionStore";
import { startNewConversation } from "@/services/multi-agent/openConversation";
import { closeSession, describeLaneV2Error, isLaneV2Error } from "@/services/multi-agent/laneV2/api";
import { useMergedSessions } from "@/hooks/multi-agent";
import { humanizeStatus } from "@/services/multi-agent";
import { StatusDot, type DotStatus } from "@/components/atoms/multi-agent/StatusDot";
import type { MergedSessionRow } from "@/services/multi-agent/laneV2/sessionListing";

const STATUS_TO_DOT: Record<string, DotStatus> = {
  done:               "ok",
  failed:             "err",
  cancelled:          "neutral",
  running:            "busy",
  queued:             "warn",
  awaiting_approval:  "warn",
};

function formatAge(iso?: string | null): string {
  if (!iso) return "—";
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return "—";
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

interface HistoryDrawerProps {
  open: boolean;
  onClose: () => void;
}

export function HistoryDrawer({ open, onClose }: HistoryDrawerProps) {
  // The ACTIVE SESSION highlights the open row.
  const v2ActiveSessionId = useLaneV2SessionStore((s) => (s.draft ? null : s.sessionId));

  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const { rows, hasV2, showClosed, setShowClosed, refetch } = useMergedSessions({ enabled: true });

  /*
   * Move focus to the close button when the drawer opens — `{ preventScroll: true }`, and that flag
   * is load-bearing, not defensive.
   *
   * REPORTED: opening history visibly flickers the whole chat column; closing it is smooth.
   * Measured, not guessed — instrumented `scroll` events on every ancestor showed the drawer's own
   * `.ma-drawer[data-open="true"]` transition (`transform`/`opacity`, `--ma-motion-lg` = 240ms) still
   * mid-flight at the 50ms mark, so the close button's `getBoundingClientRect()` still sits partly
   * outside the visible edge of the surrounding `overflow-hidden` flex row at the moment `focus()`
   * runs. A plain `.focus()` asks the browser to scroll the nearest scrollable ancestor to reveal the
   * newly-focused element — invisible on an element already fully in view, but very visible here: it
   * yanked that row's `scrollLeft` to 107px and animated it back to 0 over the next ~150ms, which
   * shifts the ENTIRE chat column (not just the drawer) sideways under the drawer's own slide-in.
   * Closing never focuses anything, so it never triggers this — which is exactly the asymmetry
   * reported. `preventScroll: true` keeps the focus move (still correct for a screen reader landing
   * in a freshly opened dialog) and drops only the scroll side effect that caused the shift.
   */
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => closeButtonRef.current?.focus({ preventScroll: true }), 50);
    return () => clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (open) refetch();
  }, [open, refetch]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const openSession = (row: MergedSessionRow) => {
    // Marks it v2, adopts it and attaches it to the lane-v2 store right away.
    openV2Session(row.id);
    onClose();
  };

  /** A v2 row's close action (R6's write half). */
  const closeV2Session = async (row: MergedSessionRow) => {
    try {
      await closeSession(row.id);
      /*
       * `useLaneV2SessionStore.getState().reset()` alone only clears the lane-v2 store — it left
       * `weaveSessionStore` still naming the just-closed session as the active conversation, so the
       * v2 route re-attached it on the next render (it is `useLaneV2Route`'s persisted fallback).
       * `startNewConversation()` is the one place that clears every store an active conversation is
       * remembered in (weave, run state, legacy, lane-v2) — the same call "New session" makes.
       */
      if (row.id === v2ActiveSessionId) startNewConversation();
      refetch();
    } catch (e) {
      if (isLaneV2Error(e) && e.code === "REVERT_AWAITING_APPROVAL") {
        toast.error(describeLaneV2Error(e));
      } else if (isLaneV2Error(e) && e.code === "SESSION_BUSY") {
        toast.error("Stop the running turn or cancel the task first.");
      } else {
        toast.error(`Could not close session: ${(e as Error)?.message ?? String(e)}`);
      }
    }
  };

  return (
    <>
      {/* Translucent overlay — click-outside to dismiss */}
      <div
        aria-hidden
        onClick={onClose}
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 20,
          background: "transparent",
          pointerEvents: open ? "auto" : "none",
        }}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Session history"
        className="ma-drawer"
        data-open={open ? "true" : "false"}
        aria-hidden={!open}
        style={{ right: 0 }}
      >
      <header
        className="flex items-center justify-between px-4 py-3"
        style={{ borderBottom: "1px solid var(--ma-border)" }}
      >
        <div className="flex items-center gap-2">
          <HistoryIcon className="h-4 w-4" style={{ color: "var(--ma-text-muted)" }} />
          <span className="ma-micro" style={{ color: "var(--ma-text)" }}>
            Sessions · {rows.length >= 40 ? "40+" : rows.length}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {hasV2 && (
            <button
              type="button"
              onClick={() => setShowClosed(!showClosed)}
              aria-pressed={showClosed}
              className="rounded-md px-1.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              title={showClosed ? "Hide closed sessions" : "Show closed sessions"}
            >
              {showClosed ? "Hide closed" : "Show closed"}
            </button>
          )}
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            title="Close history (Esc)"
            aria-label="Close history"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors hover:bg-muted"
            style={{ color: "var(--ma-text-muted)" }}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-auto px-3 py-3">
        {rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center text-muted-foreground">
            <div className="text-[15px] font-medium text-foreground">No sessions yet</div>
            <div className="text-[11px] font-medium uppercase tracking-[0.08em]">Ask a question — the session shows up here</div>
          </div>
        ) : (
          <ul className="flex flex-col gap-2">
            {rows.slice(0, 40).map((row) => {
              const isActive = row.id === v2ActiveSessionId;
              const dot = STATUS_TO_DOT[row.status || ""] ?? "neutral";
              return (
                <li key={`${row.source}:${row.id}`} className="group relative">
                  <button
                    type="button"
                    onClick={() => openSession(row)}
                    className="ma-interactive ma-fade-up flex w-full items-start gap-3 px-3 py-2.5 text-left"
                    style={{
                      borderRadius: "var(--ma-radius-md)",
                      background: isActive ? "var(--ma-grad-accent)" : "var(--ma-grad-surface)",
                      boxShadow: isActive
                        ? "var(--ma-shadow-md), inset 0 0 0 1px hsl(var(--primary) / 0.4)"
                        : "var(--ma-shadow-sm)",
                    }}
                  >
                    <span style={{ paddingTop: 5 }}>
                      <StatusDot status={dot} label={null} />
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col gap-1">
                      <span
                        className="line-clamp-2"
                        style={{
                          color: "var(--ma-text)",
                          fontSize: 13,
                          fontWeight: 500,
                          lineHeight: 1.4,
                          letterSpacing: -0.005,
                        }}
                      >
                        {row.title}
                      </span>
                      <span
                        className="ma-micro"
                        style={{ textTransform: "none", letterSpacing: 0, color: "var(--ma-text-muted)" }}
                      >
                        {`${row.subtitle ? `${row.subtitle} · ` : ""}${row.runCount ? `${row.runCount} ${row.runCount === 1 ? "turn" : "turns"} · ` : ""}`}
                        {formatAge(row.activityAt)}
                        {row.closedAt && " · closed"}
                        {row.status && !["done", "cancelled"].includes(row.status) && (
                          <span style={{ color: dot === "err" ? "var(--ma-state-err)" : dot === "busy" ? "var(--ma-accent)" : "var(--ma-state-warn)" }}>
                            {" · "}{humanizeStatus(row.status)}
                          </span>
                        )}
                      </span>
                    </span>
                  </button>
                  {!row.closedAt && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        void closeV2Session(row);
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-ma-err-soft hover:text-ma-err-text group-hover:opacity-100 focus:opacity-100"
                      title="Close session"
                      aria-label="Close session"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

    </aside>
    </>
  );
}
